import React, { useState, useEffect } from 'react';
import { dateLimitAPI, dateTimeSlotLimitAPI, eventBlockAPI, globalLimitAPI } from '../utils/limitSettings';
import './LimitSettingsModal.css';

const LimitSettingsModal = ({ show, onClose }) => {
  const [activeTab, setActiveTab] = useState('global');
  const [dateLimits, setDateLimits] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [timeSlotLimits, setTimeSlotLimits] = useState([]);
  const [eventBlocks, setEventBlocks] = useState([]);
  const [globalLimit, setGlobalLimit] = useState(3);
  const [newDateLimit, setNewDateLimit] = useState({ date: '', limit_count: 3, description: '' });
  const [newTimeSlotLimit, setNewTimeSlotLimit] = useState({ start_hour: 9, end_hour: 12, limit_count: 2, description: '' });
  const [newEventBlock, setNewEventBlock] = useState({ start_time: '', end_time: '', reason: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (show) {
      loadData();
      // 모달이 열릴 때 body 스크롤 방지
      document.body.classList.add('modal-open');
    } else {
      // 모달이 닫힐 때 body 스크롤 복원
      document.body.classList.remove('modal-open');
    }

    // 컴포넌트 언마운트 시 body 스크롤 복원
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [show]);

  useEffect(() => {
    if (selectedDate) {
      loadTimeSlotLimits(selectedDate);
    } else {
      setTimeSlotLimits([]);
    }
  }, [selectedDate]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [dateResult, blockResult, globalResult] = await Promise.all([
        dateLimitAPI.getList(),
        eventBlockAPI.getList(),
        globalLimitAPI.getCurrent()
      ]);
      
      if (dateResult.success) setDateLimits(dateResult.dateLimits);
      if (blockResult.success) setEventBlocks(blockResult.eventBlocks);
      if (globalResult.success && typeof globalResult.maximum_rdv_par_jour === 'number') {
        setGlobalLimit(globalResult.maximum_rdv_par_jour);
      }
    } catch (error) {
      console.error('데이터 로드 실패:', error);
      setError('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadTimeSlotLimits = async (date) => {
    setLoading(true);
    setError('');
    try {
      const result = await dateTimeSlotLimitAPI.getList(date);
      if (result.success) setTimeSlotLimits(result.timeSlotLimits);
    } catch (error) {
      console.error('시간대 제한 로드 실패:', error);
      setError('시간대 제한을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleGlobalLimitChange = async (e) => {
    const value = parseInt(e.target.value) || 1;
    setGlobalLimit(value);
    
    setLoading(true);
    setError('');
    try {
      const result = await globalLimitAPI.update(value);
      if (!result.success) {
        setError(result.message || '전역 제한 설정 업데이트에 실패했습니다.');
      }
    } catch (error) {
      console.error('전역 제한 설정 업데이트 실패:', error);
      setError('전역 제한 설정 업데이트에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDateLimit = async () => {
    if (!newDateLimit.date || newDateLimit.limit_count < 0) {
      setError('날짜와 제한개수를 입력해주세요.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await dateLimitAPI.create(newDateLimit);
      if (result.success) {
        setNewDateLimit({ date: '', limit_count: 3, description: '' });
        loadData();
      } else {
        setError(result.message || '날짜 제한 추가에 실패했습니다.');
      }
    } catch (error) {
      console.error('날짜 제한 추가 실패:', error);
      setError('날짜 제한 추가에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTimeSlotLimit = async () => {
    if (!selectedDate || newTimeSlotLimit.start_hour >= newTimeSlotLimit.end_hour || newTimeSlotLimit.limit_count < 0) {
      setError('날짜를 선택하고 올바른 시간대와 제한개수를 입력해주세요.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await dateTimeSlotLimitAPI.create(selectedDate, newTimeSlotLimit);
      if (result.success) {
        setNewTimeSlotLimit({ start_hour: 9, end_hour: 12, limit_count: 2, description: '' });
        loadTimeSlotLimits(selectedDate);
      } else {
        setError(result.message || '시간대 제한 추가에 실패했습니다.');
      }
    } catch (error) {
      console.error('시간대 제한 추가 실패:', error);
      setError('시간대 제한 추가에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEventBlock = async () => {
    if (!newEventBlock.start_time || !newEventBlock.end_time) {
      setError('시작일과 종료일을 입력해주세요.');
      return;
    }

    if (new Date(newEventBlock.start_time) >= new Date(newEventBlock.end_time)) {
      setError('종료일은 시작일보다 늦어야 합니다.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await eventBlockAPI.create(newEventBlock);
      if (result.success) {
        setNewEventBlock({ start_time: '', end_time: '', reason: '' });
        loadData();
      } else {
        setError(result.message || '금지일정 추가에 실패했습니다.');
      }
    } catch (error) {
      console.error('금지일정 추가 실패:', error);
      setError('금지일정 추가에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDateLimit = async (id) => {
    if (!window.confirm('이 날짜 제한을 삭제하시겠습니까? 해당 날짜의 모든 시간대별 제한도 함께 삭제됩니다.')) return;

    setLoading(true);
    setError('');
    try {
      const result = await dateLimitAPI.delete(id);
      if (result.success) {
        if (selectedDate) {
          setSelectedDate('');
          setTimeSlotLimits([]);
        }
        loadData();
      } else {
        setError(result.message || '날짜 제한 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('날짜 제한 삭제 실패:', error);
      setError('날짜 제한 삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTimeSlotLimit = async (id) => {
    if (!window.confirm('이 시간대 제한을 삭제하시겠습니까?')) return;

    setLoading(true);
    setError('');
    try {
      const result = await dateTimeSlotLimitAPI.delete(selectedDate, id);
      if (result.success) {
        loadTimeSlotLimits(selectedDate);
      } else {
        setError(result.message || '시간대 제한 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('시간대 제한 삭제 실패:', error);
      setError('시간대 제한 삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEventBlock = async (id) => {
    if (!window.confirm('이 금지일정을 삭제하시겠습니까?')) return;

    setLoading(true);
    setError('');
    try {
      const result = await eventBlockAPI.delete(id);
      if (result.success) {
        loadData();
      } else {
        setError(result.message || '금지일정 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('금지일정 삭제 실패:', error);
      setError('금지일정 삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className={`limit-settings-modal ${show ? 'show' : ''}`}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>동시일정제한 설정</h2>
          <button onClick={onClose} className="close-btn">&times;</button>
        </div>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        <div className="modal-tabs">
          <button 
            className={activeTab === 'global' ? 'active' : ''} 
            onClick={() => setActiveTab('global')}
          >
            전역 설정
          </button>
          <button 
            className={activeTab === 'date' ? 'active' : ''} 
            onClick={() => setActiveTab('date')}
          >
            특정날짜 제한
          </button>
          <button 
            className={activeTab === 'time' ? 'active' : ''} 
            onClick={() => setActiveTab('time')}
          >
            시간대별 제한
          </button>
          <button 
            className={activeTab === 'block' ? 'active' : ''} 
            onClick={() => setActiveTab('block')}
          >
            금지일정
          </button>
        </div>
        
        {activeTab === 'global' && (
          <div className="global-limits-section">
            <h3>전역 동시일정제한 설정</h3>
            <p className="help-text">전체 캘린더에 적용되는 기본 동시일정제한을 설정합니다. 특별한 설정이 없는 모든 날짜와 시간에 이 값이 적용됩니다.</p>
            
            <div className="global-setting">
              <label htmlFor="global-limit">동시일정제한 개수:</label>
              <div className="global-input-group">
                <input 
                  id="global-limit"
                  type="number" 
                  min="1"
                  max="10"
                  value={globalLimit}
                  onChange={handleGlobalLimitChange}
                  disabled={loading}
                />
                <span className="unit">개</span>
              </div>
              <p className="setting-description">
                현재 설정: <strong>{globalLimit}개</strong>의 동시일정 허용
              </p>
            </div>
            
            <div className="priority-info">
              <h4>우선순위 안내</h4>
              <ol>
                <li><strong>금지일정</strong> - 예약 완전 차단</li>
                <li><strong>특정날짜의 시간대별 제한</strong> - 특정 날짜의 특정 시간대</li>
                <li><strong>특정날짜 제한</strong> - 특정 날짜 전체</li>
                <li><strong>전역 설정</strong> - 기본값 (현재: {globalLimit}개)</li>
              </ol>
            </div>
          </div>
        )}
        
        {activeTab === 'date' && (
          <div className="date-limits-section">
            <h3>특정날짜별 제한 설정</h3>
            <p className="help-text">특정 날짜에만 다른 동시일정제한을 적용합니다. (예: 공휴일, 특별한 이벤트 날짜)</p>
            
            <div className="add-form">
              <input 
                type="date" 
                value={newDateLimit.date}
                onChange={(e) => setNewDateLimit({...newDateLimit, date: e.target.value})}
                placeholder="날짜 선택"
              />
              <input 
                type="number" 
                min="0"
                value={newDateLimit.limit_count}
                onChange={(e) => setNewDateLimit({...newDateLimit, limit_count: parseInt(e.target.value) || 0})}
                placeholder="제한개수"
              />
              <input 
                type="text"
                value={newDateLimit.description}
                onChange={(e) => setNewDateLimit({...newDateLimit, description: e.target.value})}
                placeholder="설명 (선택사항)"
              />
              <button onClick={handleAddDateLimit} disabled={loading}>
                {loading ? '추가 중...' : '추가'}
              </button>
            </div>
            
            <div className="limits-list">
              <h4>설정된 날짜 제한</h4>
              {dateLimits.length === 0 ? (
                <p className="no-data">설정된 날짜 제한이 없습니다.</p>
              ) : (
                dateLimits.map(limit => (
                  <div key={limit.id} className="limit-item">
                    <div className="limit-info">
                      <span className="limit-date">{limit.date}</span>
                      <span className="limit-count">{limit.limit_count}개</span>
                      {limit.description && <span className="limit-description">{limit.description}</span>}
                    </div>
                    <button 
                      onClick={() => handleDeleteDateLimit(limit.id)} 
                      disabled={loading}
                      className="delete-btn"
                    >
                      삭제
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        
        {activeTab === 'time' && (
          <div className="time-slot-limits-section">
            <h3>시간대별 제한 설정</h3>
            <p className="help-text">특정 날짜를 선택한 후, 해당 날짜의 시간대별로 다른 동시일정제한을 설정합니다.</p>
            
            <div className="date-selector">
              <label>날짜 선택:</label>
              <select 
                value={selectedDate} 
                onChange={(e) => setSelectedDate(e.target.value)}
                disabled={loading}
              >
                <option value="">날짜를 선택하세요</option>
                {dateLimits.map(limit => (
                  <option key={limit.id} value={limit.date}>
                    {limit.date} {limit.description && `(${limit.description})`}
                  </option>
                ))}
              </select>
            </div>
            
            {selectedDate && (
              <>
                <div className="add-form">
                  <input 
                    type="number" 
                    min="0" 
                    max="23"
                    value={newTimeSlotLimit.start_hour}
                    onChange={(e) => setNewTimeSlotLimit({...newTimeSlotLimit, start_hour: parseInt(e.target.value) || 0})}
                    placeholder="시작시간"
                  />
                  <span className="time-separator">~</span>
                  <input 
                    type="number" 
                    min="0" 
                    max="23"
                    value={newTimeSlotLimit.end_hour}
                    onChange={(e) => setNewTimeSlotLimit({...newTimeSlotLimit, end_hour: parseInt(e.target.value) || 0})}
                    placeholder="종료시간"
                  />
                  <input 
                    type="number" 
                    min="0"
                    value={newTimeSlotLimit.limit_count}
                    onChange={(e) => setNewTimeSlotLimit({...newTimeSlotLimit, limit_count: parseInt(e.target.value) || 0})}
                    placeholder="제한개수"
                  />
                  <input 
                    type="text"
                    value={newTimeSlotLimit.description}
                    onChange={(e) => setNewTimeSlotLimit({...newTimeSlotLimit, description: e.target.value})}
                    placeholder="설명 (선택사항)"
                  />
                  <button onClick={handleAddTimeSlotLimit} disabled={loading}>
                    {loading ? '추가 중...' : '추가'}
                  </button>
                </div>
                
                <div className="limits-list">
                  <h4>{selectedDate}의 시간대별 제한</h4>
                  {timeSlotLimits.length === 0 ? (
                    <p className="no-data">설정된 시간대 제한이 없습니다.</p>
                  ) : (
                    timeSlotLimits.map(limit => (
                      <div key={limit.id} className="limit-item">
                        <div className="limit-info">
                          <span className="limit-time">{limit.start_hour}시~{limit.end_hour}시</span>
                          <span className="limit-count">{limit.limit_count}개</span>
                          {limit.description && <span className="limit-description">{limit.description}</span>}
                        </div>
                        <button 
                          onClick={() => handleDeleteTimeSlotLimit(limit.id)} 
                          disabled={loading}
                          className="delete-btn"
                        >
                          삭제
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'block' && (
          <div className="event-blocks-section">
            <h3>금지일정 설정</h3>
            <p className="help-text">예약을 완전히 차단할 기간을 설정합니다. 해당 기간에는 어떤 예약도 불가능합니다.</p>
            
            <div className="add-form">
              <input 
                type="date" 
                value={newEventBlock.start_time}
                onChange={(e) => setNewEventBlock({...newEventBlock, start_time: e.target.value})}
                placeholder="시작일"
              />
              <span className="time-separator">~</span>
              <input 
                type="date" 
                value={newEventBlock.end_time}
                onChange={(e) => setNewEventBlock({...newEventBlock, end_time: e.target.value})}
                placeholder="종료일"
              />
              <input 
                type="text"
                value={newEventBlock.reason}
                onChange={(e) => setNewEventBlock({...newEventBlock, reason: e.target.value})}
                placeholder="사유 (선택사항)"
              />
              <button onClick={handleAddEventBlock} disabled={loading}>
                {loading ? '추가 중...' : '추가'}
              </button>
            </div>
            
            <div className="limits-list">
              <h4>설정된 금지일정</h4>
              {eventBlocks.length === 0 ? (
                <p className="no-data">설정된 금지일정이 없습니다.</p>
              ) : (
                eventBlocks.map(block => (
                  <div key={block.id} className="limit-item">
                    <div className="limit-info">
                      <span className="limit-date">{block.start_time} ~ {block.end_time}</span>
                      {block.reason && <span className="limit-description">{block.reason}</span>}
                    </div>
                    <button 
                      onClick={() => handleDeleteEventBlock(block.id)} 
                      disabled={loading}
                      className="delete-btn"
                    >
                      삭제
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LimitSettingsModal;
